import tinycolor from 'tinycolor2'

import { EMPTY_OBJECT } from '../constants/common'
import {
  THEME_DARK_CONTENT_NEUTRAL_LIFT_MIX,
  THEME_DARK_CONTENT_NEUTRAL_SATURATION,
  THEME_DARK_CONTENT_TINT_LIGHTNESS,
  THEME_DARK_CONTENT_TINT_SATURATION,
  THEME_DARK_LIGHT_HEADER_LUMINANCE,
  THEME_DARK_LINE_COLOR,
  THEME_DARK_ON_ACCENT_TEXT_COLOR,
  THEME_DARK_PRESSED_SURFACE_MIX,
  THEME_DARK_RAISED_COLOR,
  THEME_DARK_READABLE_ADJUSTMENT,
  THEME_DARK_READABLE_MAX_STEPS,
  THEME_DARK_READABLE_STEP,
  THEME_DARK_SURFACE_COLOR,
  THEME_DARK_TEXT_COLOR,
  THEME_DARK_TEXT_CONTRAST,
  THEME_DARK_TINT_MIX,
} from '../constants/themeMode'

import { isThemeDark } from './themeMode'

function getDarkReadableColor(
  color,
  {
    adjustment = THEME_DARK_READABLE_ADJUSTMENT.lighten,
    background = THEME_DARK_SURFACE_COLOR,
  } = EMPTY_OBJECT
) {
  return Array.from({ length: THEME_DARK_READABLE_MAX_STEPS })
    .reduce(
      result =>
        tinycolor.readability(result, background) < THEME_DARK_TEXT_CONTRAST
          ? result[adjustment](THEME_DARK_READABLE_STEP)
          : result,
      tinycolor(color)
    )
    .toHexString()
}

function getInvertedNeutralColor(color) {
  const { h, l, s } = tinycolor(color).toHsl()

  if (s >= THEME_DARK_CONTENT_NEUTRAL_SATURATION) {
    return color
  }

  const inverted = tinycolor({ h, l: 1 - l, s })

  return inverted.getLuminance() >
    tinycolor(THEME_DARK_TEXT_COLOR).getLuminance()
    ? THEME_DARK_TEXT_COLOR
    : inverted.toHexString()
}

function getOnAccentTextColor(background) {
  const { dark, light } = THEME_DARK_ON_ACCENT_TEXT_COLOR

  const lightContrast = tinycolor.readability(light, background)

  return lightContrast >= THEME_DARK_TEXT_CONTRAST ||
    lightContrast >= tinycolor.readability(dark, background)
    ? light
    : dark
}

function getColorTint(color, amount) {
  return tinycolor.mix(THEME_DARK_SURFACE_COLOR, color, amount).toHexString()
}

function getColorTowardsSurface(color, amount) {
  return tinycolor.mix(color, THEME_DARK_SURFACE_COLOR, amount).toHexString()
}

export function getDarkContentTextColor(color, background) {
  const lightened = getDarkReadableColor(getInvertedNeutralColor(color), {
    background,
  })

  if (
    tinycolor.readability(lightened, background) >= THEME_DARK_TEXT_CONTRAST
  ) {
    return lightened
  }

  const darkened = getDarkReadableColor(color, {
    adjustment: THEME_DARK_READABLE_ADJUSTMENT.darken,
    background,
  })

  return tinycolor.readability(darkened, background) >
    tinycolor.readability(lightened, background)
    ? darkened
    : lightened
}

export function getDarkContentBackgroundColor(color) {
  const { h, l, s } = tinycolor(color).toHsl()

  if (s < THEME_DARK_CONTENT_NEUTRAL_SATURATION) {
    return tinycolor
      .mix(
        THEME_DARK_SURFACE_COLOR,
        THEME_DARK_TEXT_COLOR,
        (1 - l) * THEME_DARK_CONTENT_NEUTRAL_LIFT_MIX
      )
      .toHexString()
  }

  const { max, min } = THEME_DARK_CONTENT_TINT_LIGHTNESS

  return tinycolor({
    h,
    l: Math.min(max, min + (1 - l) * (max - min) * 2),
    s: s * THEME_DARK_CONTENT_TINT_SATURATION,
  }).toHexString()
}

// Add to constants
export function getDefaultElements() {
  return [
    { '--header-bg': 'header_bg' },
    { '--header-text': 'header_text' },
    { '--header-icons': 'header_icons' },
    { '--header-hover': 'header_hover' },

    { '--header-gradient-color-0': 'sidemenu_bg' },

    { '--switcher-lines': 'switcher_lines' },
    { '--switcher-icon': 'switcher_icon' },

    { '--sidemenu-hover': 'sidemenu_hover' },

    { '--submenu-hover': 'submenu_hover' },
    { '--submenu-button-bg': 'submenu_button_bg' },
    { '--submenu-button-icon': 'submenu_button_icon' },

    { '--button-bg': 'button_bg' },
    { '--pressed-button-bg': 'pressed_button_bg' },
    { '--deactive-button-bg': 'deactive_button_bg' },
    { '--deactive-button-text': 'deactive_button_text' },

    { '--active-element': 'active_element' },
    { '--pressed-element': 'pressed_element' },

    { '--link': 'link' },

    { '--counter': 'counter' },
  ]
}

export function getColor(colors) {
  const defaultElements = getDefaultElements()

  const colorList = colors.header.concat(colors.button)

  const preparedColors = defaultElements.reduce((acc, defaultColor) => {
    const obj = colorList.find(themeColor => {
      return themeColor.target.includes(Object.entries(defaultColor)[0][1])
    })

    acc[Object.entries(defaultColor)[0][0]] = obj.color

    return acc
  }, {})

  return preparedColors
}

export function getDarkColor(colors) {
  const accent = getDarkReadableColor(colors['--button-bg'])
  const pressed = getColorTowardsSurface(accent, THEME_DARK_PRESSED_SURFACE_MIX)
  const selectedBg = getColorTint(accent, THEME_DARK_TINT_MIX.selectedElementBg)

  const isLightHeader =
    tinycolor(colors['--header-bg']).getLuminance() >
    THEME_DARK_LIGHT_HEADER_LUMINANCE

  const headerText = isLightHeader
    ? getDarkReadableColor(colors['--header-text'])
    : colors['--header-text']

  const headerColors = isLightHeader
    ? {
        '--header-bg': THEME_DARK_SURFACE_COLOR,
        '--header-gradient-color-0': THEME_DARK_SURFACE_COLOR,
        '--header-hover': THEME_DARK_RAISED_COLOR,
        '--header-icons': headerText,
        '--header-text': headerText,
        '--switcher-icon': headerText,
        '--switcher-lines': THEME_DARK_LINE_COLOR,
      }
    : EMPTY_OBJECT

  return {
    ...colors,
    ...headerColors,
    '--active-element': accent,
    '--button-bg': accent,
    '--button-text': getOnAccentTextColor(accent),
    '--counter': accent,
    '--deactive-button-bg': getColorTint(
      accent,
      THEME_DARK_TINT_MIX.deactiveButtonBg
    ),
    '--deactive-button-text': getColorTint(
      accent,
      THEME_DARK_TINT_MIX.deactiveButtonText
    ),
    '--link': accent,
    '--pressed-button-bg': pressed,
    '--pressed-button-text': getOnAccentTextColor(pressed),
    '--pressed-element': pressed,
    '--selected-element-bg': selectedBg,
    '--selected-element-text': getDarkReadableColor(accent, {
      background: selectedBg,
    }),
    '--sidemenu-hover': getColorTint(accent, THEME_DARK_TINT_MIX.submenuHover),
    '--submenu-button-bg': getColorTint(
      accent,
      THEME_DARK_TINT_MIX.submenuButtonBg
    ),
    '--submenu-button-icon': accent,
    '--submenu-hover': getColorTint(accent, THEME_DARK_TINT_MIX.submenuHover),
  }
}

export function getThemeColor(colors, theme) {
  const lightColors = getColor(colors)

  return isThemeDark(theme) ? getDarkColor(lightColors) : lightColors
}
